import React, { useState } from 'react';
import VideoPostTable from '../hooks/VideoPostTable';
import { utils, writeFile } from 'xlsx';
import RowList from '../components/VideoPostRow';

const AnalyticVideoPost = () => {
    const { data, loading, error: VideoListError, reloadData, isDataFullyLoaded} = VideoPostTable();
    const [stateFilter, setStateFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('');
    const [videoFilter, setVideoFilter] = useState('');
    const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortData = (data, sortColumn, sortDirection) => {
        return [...data].sort((a, b) => {
            if (!sortColumn) return 0;

            let aValue, bValue;

            if (sortColumn === 'telegramUserId') {
                aValue = a.User.userId;
                bValue = b.User.userId;
            } else if (sortColumn === 'userName') {
                aValue = a.User.firstName;
                bValue = b.User.firstName;
            } else {
                aValue = a[sortColumn];
                bValue = b[sortColumn];
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const resetFilters = () => {
        setStateFilter('all');
        setUserFilter('');
        setVideoFilter('');
        setDateFilter({ start: '', end: '' });
        setSortColumn(null);
        setSortDirection('asc');
    };

    const filterDataByTime = (data, filter) => {
        return data.filter(item => {
            const dayStatus = item.dayStatus;
            switch (filter) {
                case 'morning': return dayStatus === 'Утро';
                case 'afternoon': return dayStatus === 'День';
                case 'evening': return dayStatus === 'Вечер';
                default: return true;
            }
        });
    };

    const filterDataByUser = (data, filter) => {
        return data.filter(item => 
            item.userId.toString().includes(filter) || 
            (item.User?.userId?.toString().includes(filter))
        );
    };

    const filterDataByVideo = (data, filter) => {
        return data.filter(item => item.videoName.toLowerCase().includes(filter.toLowerCase()));
    };

    const filterDataByDate = (data, filter) => {
        return data.filter(item => {
            const itemDate = new Date(item.timestamp);
            const startDate = filter.start ? new Date(filter.start) : null;
            const endDate = filter.end ? new Date(filter.end) : null;
            return (!startDate || itemDate >= startDate) && (!endDate || itemDate <= endDate);
        });
    };

    const filteredData = filterDataByDate(
        filterDataByVideo(
            filterDataByUser(
                filterDataByTime(data, stateFilter),
                userFilter
            ),
            videoFilter
        ),
        dateFilter
    );

    const sortedAndFilteredData = sortData(filteredData, sortColumn, sortDirection);

    const exportToExcel = (data) => {
        const filteredData = data.map(list => ({
            'User ID': list.User.userId,
            'Имя пользователя': list.User.firstName,
            'Имя видео': list.videoName,
            'Время суток': list.dayStatus,
            'Время отправления': list.timestamp,
        }));

        const worksheet = utils.json_to_sheet(filteredData);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Аналитика кнопок');
        writeFile(workbook, 'Аналитика нажатых кнопок.xlsx');
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-content">
                    <h3>Загрузка данных...</h3>
                    {data.length > 0 && (
                        <>
                            <p>Загружено {data.length} записей</p>
                            <progress value={data.length} max="7000" />
                        </>
                    )}
                    {!isDataFullyLoaded && data.length === 0 && (
                        <p>Идёт первоначальная загрузка...</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="video-analytics-container">
            <h2>Аналитика отправленных видео</h2>

            <div className="controls">
                <div className="filter-group">
                    <label htmlFor="time-filter">
                        Время суток:
                        <select
                            id="time-filter"
                            value={stateFilter}
                            onChange={(e) => setStateFilter(e.target.value)}
                        >
                            <option value="all">Все</option>
                            <option value="morning">Утро</option>
                            <option value="afternoon">День</option>
                            <option value="evening">Вечер</option>
                        </select>
                    </label>

                    <label htmlFor="user-filter">
                        User ID:
                        <input
                            id="user-filter"
                            type="text"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            placeholder="Введите id пользователя"
                        />
                    </label>

                    <label htmlFor="video-filter">
                        Название видео:
                        <input
                            id="video-filter"
                            type="text"
                            value={videoFilter}
                            onChange={(e) => setVideoFilter(e.target.value)}
                            placeholder="Введите название видео"
                        />
                    </label>

                    <label htmlFor="date-filter-start">
                        Дата:
                        <div className="date-range">
                            <input
                                id="date-filter-start"
                                type="date"
                                value={dateFilter.start}
                                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                            />
                            <span>до</span>
                            <input
                                id="date-filter-end"
                                type="date"
                                value={dateFilter.end}
                                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                            />
                        </div>
                    </label>
                </div>

                <div className="action-buttons">
                    <button onClick={resetFilters} className="reset-btn">
                        Сбросить фильтры
                    </button>
                    <button
                        onClick={() => exportToExcel(sortedAndFilteredData)}
                        className="export-btn"
                        disabled={!sortedAndFilteredData || sortedAndFilteredData.length === 0}
                    >
                        Экспорт в Excel
                    </button>

                    <button onClick={reloadData} className="refresh-btn">
                        <i className='bx bx-refresh'></i>
                    </button>
                </div>
            </div>
            
            {sortedAndFilteredData.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('courseNumber')}>
                                    № {sortColumn === 'courseNumber' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('telegramUserId')}>
                                    User ID {sortColumn === 'telegramUserId' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('userName')}>
                                    Имя пользователя {sortColumn === 'userName' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('videoName')}>
                                    Имя видео {sortColumn === 'videoName' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('dayStatus')}>
                                    Время суток {sortColumn === 'dayStatus' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('timestamp')}>
                                    Время отправления {sortColumn === 'timestamp' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAndFilteredData.map((row, index) => (
                                <RowList
                                    key={index}
                                    telegramUserId={row.User.userId}
                                    userName={row.User.firstName}
                                    videoName={row.videoName}
                                    dayStatus={row.dayStatus}
                                    timestamp={new Date(row.timestamp).toLocaleString()}
                                    courseNumber={index + 1}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>User ID</th>
                                <th>Имя пользователя</th>
                                <th>Имя видео</th>
                                <th>Время суток</th>
                                <th>Время отправления</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan="6" className="no-data">
                                    Нет данных для отображения
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <style>{`
                .video-analytics-container {
                    padding: 20px;
                    font-family: Arial, sans-serif;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                h2 {
                    margin-bottom: 20px;
                    color: #333;
                }
                
                .controls {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 15px;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                }
                
                .filter-group {
                    display: flex;
                    gap: 15px;
                    flex-wrap: wrap;
                    align-items: flex-end;
                }
                
                .filter-group label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: 14px;
                }
                
                .filter-group input, 
                .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 14px;
                }
                
                .date-range {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                
                .date-range span {
                    font-size: 12px;
                    color: #6c757d;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 10px;
                    align-items: flex-end;
                }
                
                button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 14px;
                }
                
                button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .reset-btn {
                    background-color: #6c757d;
                    color: white;
                }
                
                .export-btn {
                    background-color: #28a745;
                    color: white;
                }
                
                .refresh-btn {
                    background-color: #17a2b8;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                }
                
                button:hover:not(:disabled) {
                    opacity: 0.9;
                    transform: translateY(-1px);
                }
                
                .table-container {
                    overflow-x: auto;
                    border-radius: 8px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.05);
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                    background: white;
                }
                
                th, td {
                    padding: 12px 15px;
                    text-align: left;
                    border-bottom: 1px solid #dee2e6;
                }
                
                th {
                    background-color: #f8f9fa;
                    cursor: pointer;
                    position: relative;
                    font-weight: 600;
                    color: #495057;
                }
                
                th:hover {
                    background-color: #e9ecef;
                }
                
                tr:hover {
                    background-color: #f8f9fa;
                }
                
                .no-data {
                    text-align: center;
                    color: #6c757d;
                    padding: 20px;
                }
                
                .loading {
                    padding: 20px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin: 20px 0;
                }
                
                @media (max-width: 768px) {
                    .controls {
                        flex-direction: column;
                    }
                    
                    .action-buttons {
                        justify-content: flex-end;
                    }
                    
                    .filter-group label {
                        width: 100%;
                    }
                    
                    .date-range {
                        width: 100%;
                    }
                }

            `}</style>
        </div>
    );
};

export default AnalyticVideoPost;